import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { ROLES } from '@bcc/shared';
import { supabase } from '../lib/supabase';
import { resolvePostLoginRoute } from '../lib/postLoginRoute';
import { useAuth } from '../contexts/AuthContext';

export function Login() {
  const nav = useNavigate();
  const loc = useLocation() as { state?: { redirect?: string } };
  const [searchParams, setSearchParams] = useSearchParams();
  const { session, profile, loading: authLoading, syncAfterSignIn } = useAuth();
  const reduceMotion = useReducedMotion();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [confirmedFromHash, setConfirmedFromHash] = useState(false);
  const [confirmedBannerDismissed, setConfirmedBannerDismissed] = useState(false);

  const querySaysConfirmed = ['1', 'true', 'yes', 'email'].includes(
    (searchParams.get('confirmed') ?? '').toLowerCase(),
  );

  useEffect(() => {
    const readHash = () => {
      try {
        const hp = new URLSearchParams(window.location.hash.replace(/^#/, ''));
        if (hp.get('type') === 'signup') setConfirmedFromHash(true);
      } catch {
        /* noop */
      }
    };
    readHash();
    /** After Supabase processes the URL fragment, type may still be visible on first paint. */
    const id = window.setTimeout(readHash, 50);
    return () => clearTimeout(id);
  }, []);

  /** Already signed in (e.g. bounced from RequireAuth, or bookmarked /login) — go to portal. */
  useEffect(() => {
    if (authLoading) return;
    if (!session || !profile) return;
    nav(resolvePostLoginRoute(loc.state?.redirect, profile.role), { replace: true });
  }, [authLoading, session, profile, loc.state?.redirect, nav]);

  const showConfirmedBanner = !confirmedBannerDismissed && (querySaysConfirmed || confirmedFromHash);

  const dismissConfirmedBanner = () => {
    setConfirmedBannerDismissed(true);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('confirmed');
        return next;
      },
      { replace: true },
    );
    if (window.location.hash) {
      window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);
    }
  };

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    setLoading(true);
    const { data: signData, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      setLoading(false);
      setErr(error.message);
      return;
    }
    const sess = signData.session;
    if (!sess?.access_token) {
      setLoading(false);
      setErr('No session returned. Try again.');
      return;
    }
    const userProfile = await syncAfterSignIn(sess);
    setLoading(false);
    if (!userProfile) {
      setErr('Could not load your account. Check your connection and try again.');
      return;
    }
    nav(resolvePostLoginRoute(loc.state?.redirect, userProfile.role), { replace: true });
  }

  return (
    <motion.div
      className="mx-auto max-w-md px-4 py-12 md:px-6 md:py-16"
      initial={reduceMotion ? undefined : { opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={reduceMotion ? { duration: 0 } : { duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
    >
      <div className="rounded-2xl border border-white/[0.08] bg-[var(--home-zone-dark)]/50 p-6 shadow-[0_24px_60px_-40px_rgba(0,0,0,0.55)] ring-1 ring-white/[0.04] backdrop-blur-md md:p-8">
        <div className="space-y-8">
          <h1 className="font-body text-2xl font-semibold tracking-tight text-[var(--home-text-on-dark)] md:text-3xl">
            Sign in
          </h1>

          {showConfirmedBanner ? (
            <div
              role="status"
              className="rounded-xl border border-cyan-400/35 bg-cyan-950/45 px-4 py-3 text-sm text-slate-100 shadow-lg shadow-black/25"
            >
              <p className="font-semibold text-cyan-200">Account confirmed</p>
              {session ? (
                <>
                  <p className="mt-1.5 text-slate-300">
                    You&apos;re signed in. Continue to your portal or stay here to review your details.
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Link
                      to={resolvePostLoginRoute(undefined, profile?.role ?? ROLES.INVESTOR)}
                      className="inline-flex rounded-lg bg-cyan-500 px-3 py-1.5 text-xs font-medium text-slate-950 hover:bg-cyan-400"
                    >
                      Continue to your portal
                    </Link>
                    <button
                      type="button"
                      onClick={dismissConfirmedBanner}
                      className="inline-flex rounded-lg border border-white/15 px-3 py-1.5 text-xs font-medium text-slate-200 hover:bg-white/10"
                    >
                      Dismiss
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <p className="mt-1.5 text-slate-300">Try signing in with your email and password below.</p>
                  <button
                    type="button"
                    onClick={dismissConfirmedBanner}
                    className="mt-3 text-xs font-medium text-cyan-300 underline-offset-2 hover:underline"
                  >
                    Dismiss
                  </button>
                </>
              )}
            </div>
          ) : null}

          <form className="space-y-5" onSubmit={onSubmit}>
            {err ? <p className="text-sm text-red-400">{err}</p> : null}
            <label className="flex flex-col space-y-2 text-sm">
              <span className="text-[var(--home-muted-on-dark)]">Email</span>
              <input
                required
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-xl border border-white/10 bg-black/30 px-4 py-2.5 text-[var(--home-text-on-dark)] placeholder:text-slate-500 focus:border-[var(--header-shell-bg)]/40 focus:outline-none focus:ring-2 focus:ring-[var(--header-shell-bg)]/25"
              />
            </label>
            <label className="flex flex-col space-y-2 text-sm">
              <span className="text-[var(--home-muted-on-dark)]">Password</span>
              <div className="relative">
                <input
                  required
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  className="w-full rounded-xl border border-white/10 bg-black/30 px-4 py-2.5 pr-11 text-[var(--home-text-on-dark)] placeholder:text-slate-500 focus:border-[var(--header-shell-bg)]/40 focus:outline-none focus:ring-2 focus:ring-[var(--header-shell-bg)]/25"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded-lg p-1.5 text-slate-400 hover:bg-white/10 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--home-miami-blue)]"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                </button>
              </div>
            </label>
            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-full bg-[var(--home-miami-blue)] px-8 py-3 text-sm font-semibold text-white shadow-md shadow-black/20 transition hover:brightness-110 disabled:opacity-50"
            >
              {loading ? 'Signing in…' : 'Sign in'}
            </button>
          </form>
          <p className="text-sm text-[var(--home-muted-on-dark)]">
            New here?{' '}
            <Link to="/register" className="font-medium text-[var(--home-miami-blue)] underline-offset-4 hover:underline">
              Create an account
            </Link>
          </p>
        </div>
      </div>
    </motion.div>
  );
}

function EyeIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-5" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-5" aria-hidden>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88"
      />
    </svg>
  );
}
